/**
 * QMS Reusable Form Field Components
 * Common select inputs used across QMS forms
 */

import { useTranslation } from 'react-i18next';
import type {
  QmsDepartment,
  QmsPriority,
  QmsSeverityLevel,
  QmsSubtype,
  QmsTicketStatus,
} from '../../types/qms.types';

interface BaseSelectProps {
  className?: string;
  error?: boolean;
  disabled?: boolean;
}

/** Department Select */
interface QmsDepartmentSelectProps extends BaseSelectProps {
  value: QmsDepartment | '';
  onChange: (value: QmsDepartment | '') => void;
  showEmptyOption?: boolean;
}

export function QmsDepartmentSelect({
  value,
  onChange,
  className = '',
  error = false,
  disabled = false,
  showEmptyOption = true,
}: QmsDepartmentSelectProps) {
  const { t } = useTranslation('qms');

  const baseClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-atek-blue focus:border-transparent ${
    error ? 'border-red-500' : 'border-gray-300'
  } ${className}`;

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as QmsDepartment | '')}
      className={baseClass}
      disabled={disabled}
    >
      {showEmptyOption && (
        <option value="">{t('form.placeholders.selectDepartment')}</option>
      )}
      <option value="product">{t('departmentsFull.product')}</option>
      <option value="sales_marketing">{t('departmentsFull.sales_marketing')}</option>
      <option value="finance">{t('departmentsFull.finance')}</option>
      <option value="it">{t('departmentsFull.it')}</option>
      <option value="hr">{t('departmentsFull.hr')}</option>
      <option value="quality">{t('departmentsFull.quality')}</option>
      <option value="operations">{t('departmentsFull.operations')}</option>
    </select>
  );
}

/** Priority Select */
interface QmsPrioritySelectProps extends BaseSelectProps {
  value: QmsPriority;
  onChange: (value: QmsPriority) => void;
}

export function QmsPrioritySelect({
  value,
  onChange,
  className = '',
  error = false,
  disabled = false,
}: QmsPrioritySelectProps) {
  const { t } = useTranslation('qms');

  const baseClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-atek-blue focus:border-transparent ${
    error ? 'border-red-500' : 'border-gray-300'
  } ${className}`;

  return (
    <select
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value) as QmsPriority)}
      className={baseClass}
      disabled={disabled}
    >
      <option value={1}>{t('priority.1')}</option>
      <option value={2}>{t('priority.2')}</option>
      <option value={3}>{t('priority.3')}</option>
      <option value={4}>{t('priority.4')}</option>
      <option value={5}>{t('priority.5')}</option>
    </select>
  );
}

/** Severity Level Select */
interface QmsSeveritySelectProps extends BaseSelectProps {
  value: QmsSeverityLevel;
  onChange: (value: QmsSeverityLevel) => void;
}

export function QmsSeveritySelect({
  value,
  onChange,
  className = '',
  error = false,
  disabled = false,
}: QmsSeveritySelectProps) {
  const { t } = useTranslation('qms');

  const baseClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-atek-blue focus:border-transparent ${
    error ? 'border-red-500' : 'border-gray-300'
  } ${className}`;

  return (
    <select
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value) as QmsSeverityLevel)}
      className={baseClass}
      disabled={disabled}
    >
      <option value={0}>{t('severity.level0')}</option>
      <option value={1}>{t('severity.level1')}</option>
      <option value={2}>{t('severity.level2')}</option>
    </select>
  );
}

/** Subtype Select */
interface QmsSubtypeSelectProps extends BaseSelectProps {
  value: QmsSubtype | '';
  onChange: (value: QmsSubtype | '') => void;
  showEmptyOption?: boolean;
}

export function QmsSubtypeSelect({
  value,
  onChange,
  className = '',
  error = false,
  disabled = false,
  showEmptyOption = true,
}: QmsSubtypeSelectProps) {
  const { t } = useTranslation('qms');

  const baseClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-atek-blue focus:border-transparent ${
    error ? 'border-red-500' : 'border-gray-300'
  } ${className}`;

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as QmsSubtype | '')}
      className={baseClass}
      disabled={disabled}
    >
      {showEmptyOption && (
        <option value="">{t('form.placeholders.selectSubtype')}</option>
      )}
      <option value="problem">{t('subtype.problem')}</option>
      <option value="improvement">{t('subtype.improvement')}</option>
    </select>
  );
}

/** Status Select */
interface QmsStatusSelectProps extends BaseSelectProps {
  value: QmsTicketStatus;
  onChange: (value: QmsTicketStatus) => void;
  canApproveStatus?: boolean;
}

export function QmsStatusSelect({
  value,
  onChange,
  className = '',
  error = false,
  disabled = false,
  canApproveStatus = false,
}: QmsStatusSelectProps) {
  const { t } = useTranslation('qms');

  const baseClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-atek-blue focus:border-transparent ${
    error ? 'border-red-500' : 'border-gray-300'
  } ${className}`;

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as QmsTicketStatus)}
      className={baseClass}
      disabled={disabled}
    >
      <option value="client_submitted">{t('status.client_submitted', 'Client Submitted')}</option>
      <option value="submitted">{t('status.submitted')}</option>
      <option value="in_progress">{t('status.in_progress')}</option>
      <option value="ready_to_test">{t('status.ready_to_test')}</option>
      <option value="complete">{t('status.complete')}</option>
      <option value="validated_closed">{t('status.validated_closed')}</option>
      <option value="pending_review">{t('status.pending_review')}</option>
      <option value="approved" disabled={!canApproveStatus}>{t('status.approved')}</option>
      <option value="closed">{t('status.closed')}</option>
    </select>
  );
}

/** Change Type Select */
interface QmsChangeTypeSelectProps extends BaseSelectProps {
  value: string;
  onChange: (value: string) => void;
  showEmptyOption?: boolean;
}

export function QmsChangeTypeSelect({
  value,
  onChange,
  className = '',
  error = false,
  disabled = false,
  showEmptyOption = true,
}: QmsChangeTypeSelectProps) {
  const { t } = useTranslation('qms');

  const baseClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-atek-blue focus:border-transparent ${
    error ? 'border-red-500' : 'border-gray-300'
  } ${className}`;

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={baseClass}
      disabled={disabled}
    >
      {showEmptyOption && (
        <option value="">{t('form.placeholders.selectChangeType')}</option>
      )}
      <option value="minor">{t('changeControl.types.minor')}</option>
      <option value="major">{t('changeControl.types.major')}</option>
      <option value="critical">{t('changeControl.types.critical')}</option>
    </select>
  );
}
